import React from "react";
import {
  ArrowRight,
  Hammer,
  Home,
  PaintBucket,
  Ruler,
  Wrench,
  Zap,
} from "lucide-react";
import { LinkAnchor } from "../../../components/ui/link";
import { Section, SectionHeader } from "../../../components/ui/section";
import { cn } from "../../../lib/utils";
import type { Service } from "../types";
import {
  CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
} from "../layoutRadius";

type ServicesSectionProps = {
  services: Service[];
  /** When false, service cards link to #contact without service prefill. Default true. */
  quoteFormEnabled?: boolean;
};

const ICONS = [Home, Hammer, PaintBucket, Wrench, Ruler, Zap];

export const ServicesSection: React.FC<ServicesSectionProps> = ({
  services,
  quoteFormEnabled = true,
}) =>
  services.length > 0 ? (
    <Section id="services" className="bg-card">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          center
          className="mb-16"
          kicker="What We Do"
          title="Our professional services"
        />

        <ul className="grid gap-6 sm:grid-cols-2 lg:gap-8">
          {services.map((service, index) => {
            const Icon = ICONS[index % ICONS.length]!;
            return (
              <li key={service.name}>
                <div
                  className={cn(
                    "group border border-border bg-card p-6 transition-all duration-300 hover:border-accent/30 hover:shadow-card sm:p-8",
                    CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
                  )}
                >
                  <div
                    className={cn(
                      "mb-5 flex h-12 w-12 items-center justify-center bg-accent/10 transition-colors duration-300 group-hover:bg-accent",
                      CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                    )}
                  >
                    <Icon className="h-6 w-6 text-accent transition-colors duration-300 group-hover:text-accent-foreground" />
                  </div>
                  <h3 className="mb-3 font-display text-xl font-semibold text-foreground">
                    {service.name}
                  </h3>
                  <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
                    {service.summary ||
                      "Professional service tailored to your needs."}
                  </p>
                  <LinkAnchor
                    href={
                      quoteFormEnabled
                        ? `#contact?service=${encodeURIComponent(service.name)}`
                        : "#contact"
                    }
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-primary hover:no-underline"
                  >
                    {quoteFormEnabled ? "Request Quote" : "Contact us"}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </LinkAnchor>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Section>
  ) : null;
